/**
 * 외부 HTML을 CKEditor 5가 안전하게 처리할 수 있는 형태로 정규화
 * - 시맨틱 태그를 div로 변환
 * - 스크립트 태그 제거 (보안)
 * - data-* 속성 제거 (선택적, 필요시 주석 해제)
 * - font 태그를 span으로 변환
 * - 인라인 font-size 스타일 제거 (CKEditor Font 플러그인과의 충돌 방지)
 *
 * @param {string} html - 정규화할 HTML 문자열
 * @param {Object} options - 정규화 옵션
 * @param {boolean} options.removeDataAttributes - data-* 속성 제거 여부 (기본: true)
 * @param {boolean} options.removeFontSize - font-size 인라인 스타일 제거 여부 (기본: true)
 * @param {boolean} options.forceResponsiveTables - table/td/th/col/colgroup의 인라인 width·min-width 및 width 속성 제거 (본문 폭 초과·캡처 잘림 방지, 기본: false)
 * @param {boolean} options.stripNegativeTextIndent - 음수 text-indent 제거 (뷰어에서 좌측 잘림 방지, 기본: false)
 * @param {boolean} options.stripTableMarginLeft - 테이블 인라인 스타일에서 margin-left 제거 (가로 스크롤 방지, 기본: true)
 * @returns {string} 정규화된 HTML 문자열
 */
function normalizeHtmlForCKEditor(html, options = {}) {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const {
    removeDataAttributes = true,
    removeFontSize = true,
    forceResponsiveTables = false,
    stripNegativeTextIndent = false,
    stripTableMarginLeft = true,
  } = options;

  try {
    // DOMParser로 안전하게 파싱
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // 1. 스크립트 태그 제거 (보안)
    const scripts = doc.querySelectorAll('script');
    scripts.forEach((script) => script.remove());

    // 2. 시맨틱 태그를 div로 변환
    const semanticTags = ['section', 'article', 'header', 'footer', 'nav', 'aside', 'main'];
    semanticTags.forEach((tag) => {
      const elements = doc.querySelectorAll(tag);
      elements.forEach((el) => {
        const div = doc.createElement('div');
        // 모든 속성 복사 (data-* 속성 제거 옵션에 따라)
        Array.from(el.attributes).forEach((attr) => {
          if (removeDataAttributes && attr.name.startsWith('data-')) {
            return; // data-* 속성 제거
          }
          div.setAttribute(attr.name, attr.value);
        });
        // 자식 노드 이동
        while (el.firstChild) {
          div.appendChild(el.firstChild);
        }
        // 교체
        el.parentNode.replaceChild(div, el);
      });
    });

    // 3. <font> 태그를 <span>으로 변환 (레거시 HTML 호환)
    const fontElements = doc.querySelectorAll('font');
    fontElements.forEach((fontEl) => {
      const span = doc.createElement('span');
      // font 속성을 style로 변환 (color, face는 유지, size는 제거)
      const color = fontEl.getAttribute('color');
      const face = fontEl.getAttribute('face');
      // size 속성은 제거하여 CKEditor가 제어하도록 함

      let style = '';
      if (color) {
        style += `color: ${color};`;
      }
      if (face) {
        style += `font-family: ${face};`;
      }
      if (style) {
        span.setAttribute('style', style);
      }

      // 자식 노드 이동
      while (fontEl.firstChild) {
        span.appendChild(fontEl.firstChild);
      }
      // 교체
      fontEl.parentNode.replaceChild(span, fontEl);
    });

    // 4. 인라인 font-size 스타일 제거 (CKEditor Font 플러그인과의 충돌 방지)
    // 테이블 셀은 제외 - 테이블 레이아웃에 font-size가 필요할 수 있음
    if (removeFontSize) {
      const elementsWithStyle = doc.querySelectorAll('[style]');
      elementsWithStyle.forEach((el) => {
        const tagName = el.tagName.toLowerCase();
        // 테이블 관련 태그는 font-size 유지 (레이아웃 테이블의 경우)
        if (['table', 'tr', 'td', 'th', 'thead', 'tbody', 'tfoot'].includes(tagName)) {
          return;
        }

        const style = el.getAttribute('style');
        if (style && style.includes('font-size')) {
          // font-size 속성만 제거하고 나머지 스타일은 유지
          const newStyle = style
            .split(';')
            .filter((prop) => !prop.trim().toLowerCase().startsWith('font-size'))
            .join(';')
            .trim();

          if (newStyle) {
            el.setAttribute('style', newStyle);
          } else {
            el.removeAttribute('style');
          }
        }
      });
    }

    // 5. 음수 text-indent 제거 (뷰어에서 첫 줄이 컨테이너 밖으로 나가 좌측 잘림 방지)
    if (stripNegativeTextIndent) {
      const elementsWithStyle = doc.querySelectorAll('[style]');
      elementsWithStyle.forEach((el) => {
        const style = el.getAttribute('style');
        if (!style || !style.toLowerCase().includes('text-indent')) return;

        const newStyle = style
          .split(';')
          .filter((prop) => {
            const trimmed = prop.trim();
            if (!trimmed) return true;
            const lower = trimmed.toLowerCase();
            if (!lower.startsWith('text-indent')) return true;
            const valuePart = trimmed.slice(trimmed.indexOf(':') + 1).trim();
            // 음수 값이면 제거 (예: -0.25in, -1em)
            if (valuePart.startsWith('-')) return false;
            return true;
          })
          .join(';')
          .trim();

        if (newStyle) {
          el.setAttribute('style', newStyle);
        } else {
          el.removeAttribute('style');
        }
      });
    }

    // 5-1. 테이블 인라인 스타일에서 margin-left 제거 (가로 스크롤 방지)
    // margin-left 선언 및 /* margin-left: ... */ 주석 제거
    if (stripTableMarginLeft) {
      const tablesWithStyle = doc.querySelectorAll('table[style]');
      tablesWithStyle.forEach((table) => {
        let style = table.getAttribute('style');
        if (!style || !style.length) return;

        // margin-left 포함 CSS 주석 제거
        style = style.replace(/\s*\/\*[^*]*?margin-left[^*]*?\*\/\s*/gi, '');
        // margin-left 선언 제거
        style = style.replace(/\s*margin-left\s*:\s*[^;]+;?\s*/gi, '');
        // 앞뒤 공백 및 연속 세미콜론 정리
        style = style
          .replace(/\s*;\s*;/g, ';')
          .trim()
          .replace(/^;|;$/g, '')
          .trim();

        if (style) {
          table.setAttribute('style', style);
        } else {
          table.removeAttribute('style');
        }
      });
    }

    // 5-2. 표 고정 가로 폭 제거 → 에디터/뷰어/캡처 본문 폭에 맞춤 (예: 외부 붙여넣기 px 합이 734px 초과)
    if (forceResponsiveTables) {
      doc.querySelectorAll('table, td, th, col, colgroup').forEach((el) => {
        const style = el.getAttribute('style');
        if (style && style.length) {
          const newStyle = style
            .split(';')
            .map((p) => p.trim())
            .filter(Boolean)
            .filter((prop) => {
              const name = prop.split(':')[0].trim().toLowerCase();
              return name !== 'width' && name !== 'min-width';
            })
            .join('; ')
            .trim();
          if (newStyle) {
            el.setAttribute('style', newStyle);
          } else {
            el.removeAttribute('style');
          }
        }
        el.removeAttribute('width');
      });
    }

    // 6. data-* 속성 제거 (모든 요소에서, 테이블은 제외 - 테이블 구조 보존)
    if (removeDataAttributes) {
      const allElements = doc.querySelectorAll(
        '*:not(table):not(tr):not(td):not(th):not(thead):not(tbody):not(tfoot):not(col):not(colgroup)',
      );
      allElements.forEach((el) => {
        Array.from(el.attributes)
          .filter((attr) => attr.name.startsWith('data-'))
          .forEach((attr) => {
            el.removeAttribute(attr.name);
          });
      });
    }

    // 7. body의 innerHTML 반환
    return doc.body.innerHTML;
  } catch (error) {
    console.error('[normalizeHtmlForCKEditor] HTML 정규화 실패:', error);
    // 파싱 실패 시 원본 반환 (안전장치)
    return html;
  }
}

export default normalizeHtmlForCKEditor;
